/**
 * ============================================================================
 * PMTray — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /equipment/pm
 * Purpose: queue of upcoming, overdue, and recently completed PM tasks.
 *          Mirrors the CalibrationTray layout: three tabs (Due This Week /
 *          Overdue / Recently Completed) with one CTA per card opening the
 *          RecordPMDialog.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './PMTray.module.css';
import {
  usePMTrayQuery,
  type PMTask,
  type PMTaskBucket,
} from './usePMTrayQuery';
import { PMTaskCard } from './PMTaskCard';
import { RecordPMDialog } from './RecordPMDialog';

const TABS: PMTaskBucket[] = [
  'DUE_THIS_WEEK',
  'OVERDUE',
  'RECENTLY_COMPLETED',
];

const TAB_LABEL: Record<PMTaskBucket, string> = {
  DUE_THIS_WEEK: 'Due this week',
  OVERDUE: 'Overdue',
  RECENTLY_COMPLETED: 'Recently completed',
};

export function PMTray() {
  const { t, i18n } = useTranslation();
  const tasksQ = usePMTrayQuery();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const [tab, setTab] = useState<PMTaskBucket>('DUE_THIS_WEEK');
  const [target, setTarget] = useState<PMTask | null>(null);

  const all: PMTask[] = tasksQ.data?.items ?? [];
  const counts = tasksQ.data?.counts ?? {
    DUE_THIS_WEEK: 0,
    OVERDUE: 0,
    RECENTLY_COMPLETED: 0,
  };

  const visible = useMemo(
    () => all.filter((task) => task.bucket === tab),
    [all, tab],
  );

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('equipment.pmTray.eyebrow', { defaultValue: 'Equipment' })}
        title={t('equipment.pmTray.title', { defaultValue: 'PM tray' })}
        subtitle={t('equipment.pmTray.subtitle', {
          defaultValue:
            'Preventive-maintenance tasks awaiting recording. A COMPLETED outcome requires a doc-controller co-signature.',
        })}
      />

      <Card>
        <CardHeader>
          <ul
            className={styles.tabs}
            role="tablist"
            aria-label={t('equipment.pmTray.tabsAria', {
              defaultValue: 'PM bucket',
            })}
          >
            {TABS.map((k) => {
              const active = tab === k;
              return (
                <li key={k} role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls={`pm-tab-panel-${k}`}
                    id={`pm-tab-${k}`}
                    tabIndex={active ? 0 : -1}
                    className={`${styles.tabButton} ${active ? styles.tabButtonActive : ''}`}
                    onClick={() => setTab(k)}
                  >
                    <span>
                      {t(`equipment.pmTray.tabs.${k}`, {
                        defaultValue: TAB_LABEL[k],
                      })}
                    </span>
                    <span className={styles.tabBadge} aria-hidden="true">
                      {nf.format(counts[k] ?? 0)}
                    </span>
                    <span className="sr-only">
                      {t('equipment.pmTray.tabsCountAria', {
                        defaultValue: '({{count}} tasks)',
                        count: counts[k] ?? 0,
                      })}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </CardHeader>
        <CardBody>
          <div
            role="tabpanel"
            id={`pm-tab-panel-${tab}`}
            aria-labelledby={`pm-tab-${tab}`}
          >
            {tasksQ.isLoading && (
              <div
                role="status"
                aria-live="polite"
                aria-label={t('common.loading', { defaultValue: 'Loading…' })}
              >
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={styles.skeletonCard}
                    aria-hidden="true"
                  />
                ))}
              </div>
            )}

            {tasksQ.isError && (
              <div className={styles.errorBanner} role="alert">
                <span>
                  {t('equipment.pmTray.errors.loadFailed', {
                    defaultValue: 'Could not load PM tasks.',
                  })}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => tasksQ.refetch()}
                >
                  {t('equipment.pmTray.retry', { defaultValue: 'Retry' })}
                </Button>
              </div>
            )}

            {!tasksQ.isLoading && !tasksQ.isError && visible.length === 0 && (
              <p className={styles.emptyState}>
                {t('equipment.pmTray.empty', {
                  defaultValue: 'No PM tasks in this bucket.',
                })}
              </p>
            )}

            {visible.length > 0 && (
              <ul className={styles.cardList}>
                {visible.map((task) => (
                  <li key={task.task_id}>
                    <PMTaskCard
                      task={task}
                      onRecord={(tk) => setTarget(tk)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardBody>
      </Card>

      {target && (
        <RecordPMDialog
          task={target}
          onClose={() => setTarget(null)}
          onRecorded={() => {
            setTarget(null);
            tasksQ.refetch();
          }}
        />
      )}
    </div>
  );
}
